import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { type Channel, channelApiClient } from '@src/api/shared/community/channelApiClient.ts';

export const loadChannels = createAsyncThunk('channel/load', async () => {
  const res = await channelApiClient.index({ with_unread: true });
  let channels = [] as Channel[];
  handleApiResponse(res, (data) => {
    channels = data;
  });
  return channels;
});

const channelSlice = createSlice({
  name: 'channel',
  initialState: {
    channels: [] as Channel[],
    preservedUnreadLines: {} as Record<string, number>,
    // 現在開いているチャンネルのコード。loadChannels でのバッジ復活を防ぐために使用する
    activeChannelCode: null as string | null,
  },
  reducers: {
    resetChannelCounts: (state, action: PayloadAction<string>) => {
      const channelCode = action.payload;
      const target = state.channels.find((c) => c.code === channelCode);
      if (target) {
        target.unread_count = 0;
        target.mention_count = '';
        target.has_unread = false;
        target.has_mention = false;
        target.first_unread_chat_id = null;
      }
      state.activeChannelCode = channelCode;
    },
    addChannel: (state, action: PayloadAction<Channel>) => {
      const channel = action.payload;
      state.channels.push(channel);
    },
    updateChannel: (state, action: PayloadAction<Channel>) => {
      const channel = action.payload;
      state.channels = state.channels.map((c) => (c.code === channel.code ? channel : c));
    },
    removeChannel: (state, action: PayloadAction<string>) => {
      const channelCode = action.payload;
      state.channels = state.channels.filter((c) => c.code !== channelCode);
      if (state.activeChannelCode === channelCode) {
        state.activeChannelCode = null;
      }
    },
    updateChannelOrders: (
      state,
      action: PayloadAction<{ orders: { code: string; order: number }[] }>,
    ) => {
      const payload = action.payload;

      payload.orders.forEach((order) => {
        const channel = state.channels.find((c) => c.code === order.code);
        if (channel) {
          channel.sort_order = order.order;
        }
      });

      state.channels.sort((a, b) => a.sort_order - b.sort_order);
    },
    setPreservedUnreadLine: (
      state,
      action: PayloadAction<{ channelCode: string; chatId: number }>,
    ) => {
      const { channelCode, chatId } = action.payload;
      state.preservedUnreadLines[channelCode] = chatId;
    },
    clearPreservedUnreadLine: (state, action: PayloadAction<string>) => {
      const channelCode = action.payload;
      delete state.preservedUnreadLines[channelCode];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadChannels.fulfilled, (state, action) => {
      state.channels = action.payload.map((serverChannel) => {
        // アクティブなチャンネルはサーバーデータで未読・メンション数を復元しない
        if (serverChannel.code === state.activeChannelCode) {
          return {
            ...serverChannel,
            unread_count: 0,
            mention_count: '',
            has_unread: false,
            has_mention: false,
            first_unread_chat_id: null,
          };
        }
        return serverChannel;
      });
    });
  },
});

export const {
  resetChannelCounts,
  addChannel,
  updateChannel,
  updateChannelOrders,
  removeChannel,
  setPreservedUnreadLine,
  clearPreservedUnreadLine,
} = channelSlice.actions;
export default channelSlice.reducer;
